import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { UserCog, ShieldCheck, UserPlus, Loader2, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Delegasi() {
  const [data, setData] = useState({ approvers: [], candidates: [], branches: [] });
  const [divInputs, setDivInputs] = useState({});
  const [branchSel, setBranchSel] = useState({});
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/approvers").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleBranch = (userId, bid) => {
    setBranchSel((s) => {
      const cur = s[userId] || [];
      return { ...s, [userId]: cur.includes(bid) ? cur.filter((x) => x !== bid) : [...cur, bid] };
    });
  };

  const appoint = async (userId) => {
    const branch_ids = branchSel[userId] || [];
    if (branch_ids.length === 0) { toast.error("Pilih minimal satu cabang yang ditugaskan"); return; }
    setBusy(userId);
    try {
      await api.post("/approvers/appoint", { user_id: userId, divisi: divInputs[userId] || "", branch_ids });
      toast.success("Approver berhasil ditunjuk.");
      setDivInputs((s) => ({ ...s, [userId]: "" }));
      setBranchSel((s) => ({ ...s, [userId]: [] }));
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const branchName = (id) => (data.branches.find((b) => b.id === id) || {}).name || "";

  const revoke = async (userId) => {
    setBusy(userId);
    try { await api.post(`/approvers/${userId}/revoke`); toast.success("Penunjukan approver dicabut."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <UserCog className="h-6 w-6 text-brand" /> Delegasi Approver
        </h2>
        <p className="text-sm text-muted-foreground">Tunjuk atau ganti Approver yang berwenang menyetujui pengajuan kas.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Current approvers */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5 text-brand" />
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Approver Aktif ({data.approvers.length})</h3>
          </div>
          {data.approvers.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Belum ada approver yang ditunjuk.</p>
          ) : (
            <div className="space-y-2">
              {data.approvers.map((u) => (
                <div key={u.id} className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border" data-testid={`approver-${u.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {(u.approver_branch_ids || []).map((bid) => (
                      <Badge key={bid} variant="secondary" data-testid={`approver-branch-${u.id}-${bid}`}>{branchName(bid)}</Badge>
                    ))}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`revoke-btn-${u.id}`}>
                          <X className="h-4 w-4 mr-1" /> Cabut
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Cabut penunjukan?</AlertDialogTitle>
                          <AlertDialogDescription>
                            <strong>{u.name}</strong> tidak akan lagi bisa menyetujui pengajuan dan akan dikembalikan sebagai Pemohon.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Batal</AlertDialogCancel>
                          <AlertDialogAction onClick={() => revoke(u.id)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-revoke-${u.id}`}>Cabut</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Candidates */}
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus className="h-5 w-5 text-brand" />
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Tunjuk Approver Baru</h3>
          </div>
          {data.candidates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Tidak ada kandidat tersedia.</p>
          ) : (
            <div className="space-y-3">
              {data.candidates.map((u) => (
                <div key={u.id} className="px-3 py-3 rounded-lg border" data-testid={`candidate-${u.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email}{u.branch_name ? ` · ${u.branch_name}` : ""}</p>
                    </div>
                    <Input placeholder="Divisi" value={divInputs[u.id] || u.divisi || ""}
                      onChange={(e) => setDivInputs((s) => ({ ...s, [u.id]: e.target.value }))}
                      className="h-9 w-24 shrink-0" data-testid={`divisi-input-${u.id}`} />
                    <Button size="sm" onClick={() => appoint(u.id)} disabled={busy === u.id}
                      className="bg-brand hover:bg-emerald-800 shrink-0" data-testid={`appoint-btn-${u.id}`}>
                      {busy === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Tunjuk"}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mb-1.5">Cabang yang ditugaskan (pilih ≥ 1):</p>
                  <div className="flex flex-wrap gap-1.5">
                    {data.branches.map((b) => {
                      const active = (branchSel[u.id] || []).includes(b.id);
                      return (
                        <button key={b.id} type="button" onClick={() => toggleBranch(u.id, b.id)}
                          data-testid={`assign-branch-${u.id}-${b.id}`}
                          className={`text-xs px-2 py-1 rounded-full border transition-colors ${active ? "bg-brand text-white border-brand" : "bg-white text-slate-600 border-slate-200 hover:border-brand"}`}>
                          {b.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
